import { UseFilterStateResult } from '@common/hooks';
import { AnyObject, APIFacetField } from '@common/types/queryState';
import { createContext, PropsWithChildren, useContext } from 'react';
import { FacetFieldConfig, NormalizerFn, SearchResult } from '../types';
import { NORMALIZATION_STRATEGIES } from '../utils';

export interface FacetingContextValue<TEntity = AnyObject> {
    // Configuration
    visibleConfig: FacetFieldConfig<TEntity>[];
    masterConfig: FacetFieldConfig<TEntity>[];

    // Core data / state
    facetData: APIFacetField[];
    filterState: UseFilterStateResult;

    // Sticky / Pinning state
    isStickyEnabled: boolean;
    setIsStickyEnabled: (val: boolean) => void;
    hasBaseline: boolean;
    showStickyToggle?: boolean;

    // Data sets
    displayFacets: APIFacetField[]; // Sticky baseline counts
    realTimeFacets: APIFacetField[]; // Live counts

    // Totals
    totalResults: number;
    realTimeMatches: number;

    // Engine Utility
    getNormalizer: (key: string) => NormalizerFn;

    // Handlers
    onPin: (result: SearchResult) => void;
    onUnpin: (key: string) => void;

    // Label Resolvers
    getSectionLabel: (key: string) => string;
    getValueLabel: (key: string, val: string) => string;
}

const InternalContext = createContext<FacetingContextValue<never> | undefined>(
    undefined,
);

interface FacetingProviderProps<T> extends PropsWithChildren {
    value: FacetingContextValue<T>;
}

export const FacetingProvider = <T,>({
    children,
    value,
}: FacetingProviderProps<T>) => (
    <InternalContext.Provider value={value}>
        {children}
    </InternalContext.Provider>
);

// eslint-disable-next-line react-refresh/only-export-components
export const useFacetingContext = <T,>() => {
    const context = useContext(InternalContext);
    if (!context) {
        throw new Error(
            'Faceting components must be wrapped in a <FacetingProvider />',
        );
    }
    return context as FacetingContextValue<T>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const defaultGetNormalizer = (key: string): NormalizerFn => {
    return NORMALIZATION_STRATEGIES[key] || ((value: string) => value);
};
